import { submitClaim, submitReimbursedClaim, getClaimById, getClaimList, addClaimPayment, updateClaimStatusByClaimId, uploadClaimDocumentData } from "../../../service/ClaimService";
import { LoadingType } from "../../../types/CommonTypes";
import { updateElementValue } from "../../../utils/updateElementValue";
import { OrderDetails, OrderItemDTO } from "../order/OrderSlice";
import { useAppSelector } from "../redux-hooks";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export interface OrderInfo {
  orderId: number;
  practiceId: number;
  productId: number;
  ivrId: number;
  patientId: number;
  practiceName: string;
  hasMiddleName: boolean;
  patientName: string;
  provider?: string;
  productCategory?: string|null;
  additionalNotes?: string|null;
  manufacturerName: string;
  manufacturerId: number;
  productName: string;
  providerName: string;
  orderNumber: string;
  orderDate: string;
  dateOfService?: string;
  orderType: string;
  orderStatus: string;
  mfgConfirmationNumber: string;
  shippedDate: string;
  deliveredDate: string;
  processedDate: string;
  shippingCarrier: string;
  trackingNumber: string;
  poNumber: string;
  orderTotalUnit: number;
  orderTotalPrice: number;
  orderDocumentId: number;
  invoiceId?: number;
  invoiceStatus?: string;
  orderInfo?:{
    practice: string,
    patient?: string,
    provider?: string,
    PatientID?: string,
    facilityAddress: string,
    processedDate: string,
  };
  orderItems: OrderItemDTO[];
  manufacturerOrderId: string;
  manufacturerPatientId: string;
  requestedShippingMethod: string;
  requestedDeliveryDate?: string;
  claimId?: string;
  claimNumber?: string;
  cmsDocumentId?: number;
  ivrNumber?: string;
  patientDOB?: string;
  ivrLastUpdateTime: string;
};

export interface claimInfo {
    orderId: number;
    productId: number;
    claimNumber: string;
    claimStatus: 'SUBMITTED' | 'REIMBURSED'
    submittedDate: string;
    claimAmount?: string;
    orderItems: { orderItemId: number; }[]
}

export interface orderItemClaim {
    claimId: number,
    claimNumber: string,
    claimAmount?: number,
    claimStatus?: string,
    submittedDate: string,
    reimbursedDate?: string
}

export interface reimburseClaimInfo {

    claimId: number;
    claimAmount: number;
    claimStatus: 'SUBMITTED' | 'REIMBURSED';
    reimbursedDate: string;

}

export interface ClaimDTO {
    claimId: number;
    claimNumber: string;
    claimAmount: string;
    claimStatus: string;
    submittedDate: string;
    deniedDate: string;
    appealedDate: string;
    reimbursedDate: string;
    cmsDocumentId: number;
    auditUserId: number;
    auditTimestamp: string;
}

export type ClaimPaymentInfo = {
    claimId: number | null;
    primaryPayment?: number | null;
    secondaryPayment?: number | null;
    patientPortion?: number | null;
}

export interface ClaimSearchRequest {
    currentPage?: number;
    itemsPerPage?: number;
    claimStatus: string;
    claimId?: number;
    orderNumber: string;
    patientFirstName: string;
    patientLastName: string;
    providerId: string;
    fromServiceDate: string;
    toServiceDate: string;
}

interface PaginationClaimList {
    content: ClaimInfo[];
    totalElements: number;
    totalPages: number;
    size: number;
}

export interface ClaimInfo {
    claimId: number;
    order: OrderDetails;
    insuranceType: string;
    claimDueDate: string;
    claimStatus: string;
    auditUserId: number;
    auditTimestamp: string;
}

export type ClaimDocumentInfo = {
    claimId: number;
    documentId?: number;
    documentName: string;
    content: string;
    documentType?: string;
    type?: string;
    fileType: string;
    auditTimestamp?: string;
};

type ClaimState = {
    status: LoadingType;
    claimListInfo?: PaginationClaimList | null;
    claimInfo?: ClaimInfo
}

const initialState: ClaimState = {
    status: "idle",
    claimListInfo: undefined,
    claimInfo: undefined
}

export const fetchClaimList = createAsyncThunk("fetchClaimList", async (data: ClaimSearchRequest) => {
    const response = await getClaimList(data);
    return response;
})

export const addClaim = createAsyncThunk("addClaim", async (data: claimInfo) => {
    const response = await submitClaim(data);
    return response;
})

export const addReimbursedClaim = createAsyncThunk("addReimbursedClaim", async (data: reimburseClaimInfo) => {
    const response = await submitReimbursedClaim(data);

    return response;
})

export const fetchClaimById = createAsyncThunk("fetchClaimById", async (claimId: number) => {
    const response = await getClaimById(claimId);
    return response;
})

export const recordClaimPayment = createAsyncThunk("recordClaimPayment", async (data: ClaimPaymentInfo) => {
    const response = await addClaimPayment(data);
    return response;
})

export const updateClaimStatus = createAsyncThunk("updateClaimStatus", async ({ claimId, claimStatus }: { claimId: number; claimStatus: string }) => {
    const response = await updateClaimStatusByClaimId(claimId, claimStatus);
    return response;
})

export const uploadClaimDocument = createAsyncThunk(
    "uploadClaimDocument",
    async (data: any) => {
        const response = await uploadClaimDocumentData(data);
        return response.data;
    }
);

const claimSlice = createSlice({
    name: "claim",
    initialState,
    reducers: {
        updateElementInOrderSlice: updateElementValue,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClaimList.pending, (state, action) => {
                state.status = "loading"
            })
            .addCase(fetchClaimList.fulfilled, (state, action) => {
                state.status = "success"
                // state.claimListInfo = action.payload.data.data
                state.claimListInfo = action.payload?.data?.data // CS-1984
            })
            .addCase(fetchClaimList.rejected, (state, action) => {
                state.status = "error"
            })
            .addCase(addClaim.pending, (state, action) => {
                state.status = "loading"
            })
            .addCase(addClaim.fulfilled, (state, action) => {
                state.status = "success"
            })
            .addCase(addClaim.rejected, (state, action) => {
                state.status = "error"
            })
            .addCase(addReimbursedClaim.pending, (state, action) => {
                state.status = "loading"
            })
            .addCase(addReimbursedClaim.fulfilled, (state, action) => {
                state.status = "success"
            })
            .addCase(addReimbursedClaim.rejected, (state, action) => {
                state.status = "error"
            })
            .addCase(fetchClaimById.pending, (state, action) => {
                state.status = "loading"
            })
            .addCase(fetchClaimById.fulfilled, (state, action) => {
                state.status = "success"
                // state.claimInfo = action.payload.data.data
                state.claimInfo = action.payload?.data?.data // CS-1984
            })
            .addCase(fetchClaimById.rejected, (state, action) => {
                state.status = "error"
            })
    },
});

export const { updateElementInOrderSlice } = claimSlice.actions;
export const useClaimSlice = () => useAppSelector((state) => state.claimSlice);
export default claimSlice.reducer;